import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  ArrowLeft,
  Link2,
  Unlink,
  Search,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Users,
  Zap,
  User,
  Hash,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Per-tab config: what we're linking, which endpoints to call, how to label
// the right-hand list, etc. Adding a new linkable entity = add a row here.
const MODE_CONFIG = {
  members: {
    label: "Members",
    listLabel: "System Members",
    icon: Users,
    iconClass: "text-amber-400",
    linkPath: "/discord/link-member",
    unlinkPath: "/discord/unlink-member",
    autoLinkPath: "/discord/auto-link-members",
    linkPayload: (discord, entity) => ({
      discord_id: discord.discord_id,
      member_id: entity.id,
    }),
    unlinkPayload: (entity) => ({ member_id: entity.id }),
    entityKey: "system_members",
    countKey: "linked_count",
    linkedFieldOnDiscord: "linked_member",
    searchPlaceholder: "Search by handle or chapter...",
    subtitle: (m) => `${m.chapter || "—"} • ${m.title || ""}`.trim(),
  },
  prospects: {
    label: "Prospects",
    listLabel: "Prospects",
    icon: User,
    iconClass: "text-emerald-400",
    linkPath: "/discord/link-prospect",
    unlinkPath: "/discord/unlink-prospect",
    autoLinkPath: "/discord/auto-link-prospects",
    linkPayload: (discord, entity) => ({
      discord_id: discord.discord_id,
      prospect_id: entity.id,
    }),
    unlinkPayload: (entity) => ({ prospect_id: entity.id }),
    entityKey: "prospects",
    countKey: "linked_prospect_count",
    linkedFieldOnDiscord: "linked_prospect",
    searchPlaceholder: "Search prospects by handle or chapter...",
    subtitle: (m) => `${m.chapter || "—"} • Prospect`,
  },
  hangarounds: {
    label: "Hangarounds",
    listLabel: "Hangarounds",
    icon: User,
    iconClass: "text-cyan-400",
    linkPath: "/discord/link-hangaround",
    unlinkPath: "/discord/unlink-hangaround",
    autoLinkPath: "/discord/auto-link-prospects",  // shared backend route covers both
    linkPayload: (discord, entity) => ({
      discord_id: discord.discord_id,
      hangaround_id: entity.id,
    }),
    unlinkPayload: (entity) => ({ hangaround_id: entity.id }),
    entityKey: "hangarounds",
    countKey: "linked_hangaround_count",
    linkedFieldOnDiscord: "linked_hangaround",
    searchPlaceholder: "Search hangarounds by handle or name...",
    subtitle: () => "Hangaround",
  },
};

export default function DiscordLinking() {
  const navigate = useNavigate();
  const [token] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  const [autoLinking, setAutoLinking] = useState(false);
  const [mode, setMode] = useState("members");
  const [data, setData] = useState({
    discord_members: [],
    system_members: [],
    prospects: [],
    hangarounds: [],
    total_discord: 0,
    total_system: 0,
    total_prospects: 0,
    total_hangarounds: 0,
    linked_count: 0,
    linked_prospect_count: 0,
    linked_hangaround_count: 0,
  });
  const [searchDiscord, setSearchDiscord] = useState("");
  const [searchSystem, setSearchSystem] = useState("");
  const [selectedDiscord, setSelectedDiscord] = useState(null);
  const [selectedSystem, setSelectedSystem] = useState(null);
  const [showUnlinked, setShowUnlinked] = useState(true);
  const [autoLinkResults, setAutoLinkResults] = useState(null);

  const cfg = MODE_CONFIG[mode];

  useEffect(() => {
    fetchData();
  }, []);

  // Reset selection when switching tabs so users don't accidentally link
  // a discord user to the wrong entity type.
  useEffect(() => {
    setSelectedDiscord(null);
    setSelectedSystem(null);
    setSearchSystem("");
    setAutoLinkResults(null);
  }, [mode]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/discord/members-for-linking`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData((prev) => ({ ...prev, ...response.data }));
    } catch (error) {
      toast.error("Failed to fetch members");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleAutoLink = async () => {
    try {
      setAutoLinking(true);
      const response = await axios.post(`${API}${cfg.autoLinkPath}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAutoLinkResults(response.data);
      const count =
        response.data.linked_count ??
        (response.data.linked_prospect_count || 0) +
          (response.data.linked_hangaround_count || 0);
      toast.success(`Auto-linked ${count}`);
      fetchData();
    } catch (error) {
      toast.error("Auto-link failed");
    } finally {
      setAutoLinking(false);
    }
  };

  const handleLink = async () => {
    if (!selectedDiscord || !selectedSystem) {
      toast.error("Select both a Discord user and a target");
      return;
    }
    try {
      await axios.post(
        `${API}${cfg.linkPath}`,
        cfg.linkPayload(selectedDiscord, selectedSystem),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Linked ${selectedDiscord.display_name} to ${selectedSystem.handle}`);
      setSelectedDiscord(null);
      setSelectedSystem(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to link");
    }
  };

  const handleUnlink = async (entity) => {
    if (!confirm(`Unlink Discord from ${entity.handle}?`)) return;
    try {
      await axios.post(`${API}${cfg.unlinkPath}`, cfg.unlinkPayload(entity), {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Unlinked ${entity.handle}`);
      fetchData();
    } catch (error) {
      toast.error("Failed to unlink");
    }
  };

  // Discord members list (filtered)
  const filteredDiscord = useMemo(() => {
    return (data.discord_members || []).filter((m) => {
      const matchesSearch =
        !searchDiscord ||
        m.display_name?.toLowerCase().includes(searchDiscord.toLowerCase()) ||
        m.username?.toLowerCase().includes(searchDiscord.toLowerCase());
      const isLinkedToCurrent = !!m[cfg.linkedFieldOnDiscord];
      const matchesFilter = !showUnlinked || !isLinkedToCurrent;
      return matchesSearch && matchesFilter;
    });
  }, [data.discord_members, searchDiscord, showUnlinked, cfg.linkedFieldOnDiscord]);

  // Entity list for the active tab (members | prospects | hangarounds)
  const entityList = data[cfg.entityKey] || [];
  const filteredSystem = entityList.filter((m) => {
    const matchesSearch =
      !searchSystem ||
      m.handle?.toLowerCase().includes(searchSystem.toLowerCase()) ||
      (m.chapter && m.chapter.toLowerCase().includes(searchSystem.toLowerCase())) ||
      (m.name && m.name.toLowerCase().includes(searchSystem.toLowerCase()));
    const matchesFilter = !showUnlinked || !m.discord_id;
    return matchesSearch && matchesFilter;
  });

  const linkedEntities = entityList.filter((m) => m.discord_id);

  const Icon = cfg.icon;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/discord-bots")}
              className="text-slate-400 hover:text-white"
              data-testid="discord-linking-back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                <Link2 className="w-6 h-6 text-purple-400" />
                Discord Linking
              </h1>
              <p className="text-slate-400 text-sm">
                Link Members, Prospects, and Hangarounds to their Discord accounts
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={fetchData}
              disabled={loading}
              className="border-slate-600 text-slate-300"
              data-testid="discord-linking-refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button
              onClick={handleAutoLink}
              disabled={autoLinking}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="discord-linking-autolink-btn"
            >
              <Zap className={`w-4 h-4 mr-2 ${autoLinking ? "animate-pulse" : ""}`} />
              Auto-Link {cfg.label}
            </Button>
          </div>
        </div>

        {/* Tab bar */}
        <div className="flex gap-2 mb-6 border-b border-slate-700">
          {Object.entries(MODE_CONFIG).map(([key, c]) => {
            const TabIcon = c.icon;
            const isActive = mode === key;
            return (
              <button
                key={key}
                onClick={() => setMode(key)}
                data-testid={`discord-linking-tab-${key}`}
                className={`px-4 py-2 -mb-px flex items-center gap-2 text-sm font-medium transition-colors border-b-2 ${
                  isActive
                    ? "border-purple-500 text-white"
                    : "border-transparent text-slate-400 hover:text-white"
                }`}
              >
                <TabIcon className={`w-4 h-4 ${isActive ? c.iconClass : ""}`} />
                {c.label}
                <span className="ml-1 text-xs text-slate-500">
                  ({data[c.countKey] || 0}/{data[`total_${key === "members" ? "system" : key}`] || 0})
                </span>
              </button>
            );
          })}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-white" data-testid="stats-discord-total">
              {data.total_discord}
            </div>
            <div className="text-slate-400 text-sm">Discord Members</div>
          </div>
          <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-white" data-testid="stats-entity-total">
              {entityList.length}
            </div>
            <div className="text-slate-400 text-sm">{cfg.listLabel}</div>
          </div>
          <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-green-400" data-testid="stats-entity-linked">
              {data[cfg.countKey] || 0}
            </div>
            <div className="text-slate-400 text-sm">Linked</div>
          </div>
        </div>

        {/* Auto-Link Results */}
        {autoLinkResults && (
          <div className="bg-slate-800/50 border border-purple-700 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-white font-medium flex items-center gap-2">
                <Zap className="w-4 h-4 text-purple-400" />
                Auto-Link Results
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setAutoLinkResults(null)}
                className="text-slate-400"
              >
                <XCircle className="w-4 h-4" />
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-green-400 font-medium">
                  {autoLinkResults.linked_count ??
                    (autoLinkResults.linked_prospect_count || 0) +
                      (autoLinkResults.linked_hangaround_count || 0)}
                </span>
                <span className="text-slate-400"> linked</span>
              </div>
              <div>
                <span className="text-yellow-400 font-medium">
                  {autoLinkResults.skipped_count || 0}
                </span>
                <span className="text-slate-400"> need manual linking</span>
              </div>
            </div>
          </div>
        )}

        {/* Manual Linking Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Discord Members */}
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <Hash className="w-5 h-5 text-blue-400" />
                Discord Users
              </h2>
              <label className="flex items-center gap-2 text-xs text-slate-400">
                <input
                  type="checkbox"
                  checked={showUnlinked}
                  onChange={(e) => setShowUnlinked(e.target.checked)}
                  className="rounded"
                />
                Unlinked only
              </label>
            </div>
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                placeholder="Search Discord users..."
                value={searchDiscord}
                onChange={(e) => setSearchDiscord(e.target.value)}
                className="pl-9 bg-slate-900 border-slate-600 text-white text-sm"
                data-testid="discord-search-input"
              />
            </div>
            <div className="space-y-1 max-h-72 overflow-y-auto">
              {filteredDiscord.map((member) => {
                const linkedHere = member[cfg.linkedFieldOnDiscord];
                return (
                  <div
                    key={member.discord_id}
                    onClick={() => !linkedHere && setSelectedDiscord(member)}
                    data-testid={`discord-row-${member.discord_id}`}
                    className={`p-2 rounded-lg cursor-pointer transition-colors ${
                      selectedDiscord?.discord_id === member.discord_id
                        ? "bg-purple-600/30 border border-purple-500"
                        : linkedHere
                        ? "bg-green-900/20 border border-green-800/30 cursor-not-allowed"
                        : "bg-slate-900/50 hover:bg-slate-700/50 border border-transparent"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="min-w-0">
                        <div className="text-white text-sm font-medium truncate">
                          {member.display_name}
                        </div>
                        <div className="text-slate-500 text-xs truncate">@{member.username}</div>
                      </div>
                      {linkedHere && (
                        <span className="text-green-400 text-xs flex items-center gap-1 shrink-0">
                          <CheckCircle2 className="w-3 h-3" />
                          {linkedHere.handle}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
              {filteredDiscord.length === 0 && (
                <div className="text-slate-500 text-center py-4 text-sm">No users found</div>
              )}
            </div>
          </div>

          {/* Entity List (Members / Prospects / Hangarounds) */}
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <Icon className={`w-5 h-5 ${cfg.iconClass}`} />
                {cfg.listLabel}
              </h2>
            </div>
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                placeholder={cfg.searchPlaceholder}
                value={searchSystem}
                onChange={(e) => setSearchSystem(e.target.value)}
                className="pl-9 bg-slate-900 border-slate-600 text-white text-sm"
                data-testid="entity-search-input"
              />
            </div>
            <div className="space-y-1 max-h-72 overflow-y-auto">
              {filteredSystem.map((m) => (
                <div
                  key={m.id}
                  onClick={() => !m.discord_id && setSelectedSystem(m)}
                  data-testid={`entity-row-${m.id}`}
                  className={`p-2 rounded-lg cursor-pointer transition-colors ${
                    selectedSystem?.id === m.id
                      ? "bg-purple-600/30 border border-purple-500"
                      : m.discord_id
                      ? "bg-green-900/20 border border-green-800/30 cursor-not-allowed"
                      : "bg-slate-900/50 hover:bg-slate-700/50 border border-transparent"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-white text-sm font-medium">{m.handle}</div>
                      <div className="text-slate-500 text-xs">{cfg.subtitle(m)}</div>
                    </div>
                    {m.discord_id && (
                      <span className="text-green-400 text-xs flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" />
                        Linked
                      </span>
                    )}
                  </div>
                </div>
              ))}
              {filteredSystem.length === 0 && (
                <div className="text-slate-500 text-center py-4 text-sm">
                  No {cfg.label.toLowerCase()} found
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Link Button */}
        {(selectedDiscord || selectedSystem) && (
          <div className="bg-purple-900/30 border border-purple-700 rounded-lg p-4 mb-6">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4 text-sm">
                <div className="text-center">
                  <div className="text-slate-400 text-xs mb-1">Discord</div>
                  <div className="text-white font-medium">
                    {selectedDiscord?.display_name || (
                      <span className="text-slate-500">Select...</span>
                    )}
                  </div>
                </div>
                <Link2 className="w-5 h-5 text-purple-400" />
                <div className="text-center">
                  <div className="text-slate-400 text-xs mb-1">{cfg.label.replace(/s$/, "")}</div>
                  <div className="text-white font-medium">
                    {selectedSystem?.handle || <span className="text-slate-500">Select...</span>}
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSelectedDiscord(null);
                    setSelectedSystem(null);
                  }}
                  className="border-slate-600 text-slate-300"
                  data-testid="link-cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleLink}
                  disabled={!selectedDiscord || !selectedSystem}
                  className="bg-purple-600 hover:bg-purple-700"
                  data-testid="link-submit-btn"
                >
                  <Link2 className="w-4 h-4 mr-1" />
                  Link
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Linked List for current tab */}
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-green-400" />
            Linked {cfg.label} ({linkedEntities.length})
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {linkedEntities.map((entity) => {
              const discordInfo = data.discord_members.find(
                (d) => d.discord_id === entity.discord_id
              );
              return (
                <div
                  key={entity.id}
                  className="bg-slate-900/50 border border-slate-700 rounded-lg p-3 flex items-center justify-between"
                >
                  <div className="min-w-0">
                    <div className="text-white text-sm font-medium truncate">{entity.handle}</div>
                    <div className="text-slate-500 text-xs truncate">
                      {discordInfo?.display_name || entity.discord_id}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleUnlink(entity)}
                    className="text-red-400 hover:text-red-300 hover:bg-red-900/20 shrink-0 h-8 w-8"
                    data-testid={`unlink-btn-${entity.id}`}
                  >
                    <Unlink className="w-4 h-4" />
                  </Button>
                </div>
              );
            })}
          </div>
          {linkedEntities.length === 0 && (
            <div className="text-slate-500 text-center py-8">No {cfg.label.toLowerCase()} linked yet</div>
          )}
        </div>
      </div>
    </div>
  );
}
